<template>
  <div>
    <div class="d-flex align-items-center justify-content-end mt-3 mb-4">
      <!-- 匯出 Excel -->
      <div v-if="false">
        <b-button
          variant="inverse-primary"
          class="ml-0 ml-xl-4"
          @click="handleExport"
        >
          <span class="mdi mdi-file-excel mr-1"></span>
          匯出 Excel
        </b-button>
      </div>
      <b-input-group class="ml-4" style="width: 320px">
        <b-form-input
          placeholder="搜尋會員名稱、LINE UID、手機號碼"
          v-model="query.keyword"
          v-on:keyup.enter="handlePageReset"
        ></b-form-input>
        <b-input-group-append>
          <b-button @click="handlePageReset"
            ><i class="fa fa-search"></i
          ></b-button>
        </b-input-group-append>
      </b-input-group>
    </div>

    <b-table
      responsive
      striped
      hover
      :items="results"
      :fields="fields"
      @sort-changed="handleSort"
      :busy="loading"
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle"></b-spinner>
        </div>
      </template>
      <template #cell(avatar_url)="data">
        <router-link  v-if="data.item.source === 'liff'" :to="{ name: 'CustomerDetailView', params: { customerID: data.item.id } }" >
          <b-avatar
            :src="data.item.avatar_url"
            variant="secondary"
            size="2rem"
          ></b-avatar>
        </router-link>
        <b-avatar
          v-else
          :src="data.item.avatar_url"
          variant="secondary"
          size="2rem"
        ></b-avatar>
      </template>
      <template #cell(name)="data">
        <router-link v-if="data.item.source === 'liff'" :to="{ name: 'CustomerDetailView', params: { customerID: data.item.id } }" >
          {{ data.item.name }}
        </router-link>
        <span v-else>
          {{ data.item.name }}
        </span>
      </template>
    </b-table>
    <CustomPagination
      :currentPage="query.page"
      :totalRows="totalRows"
      :perPage="query.per_page"
      @page-change="handlePageChange"
      @per-page-change="handlePerPageChange"
    />
  </div>
</template>

<script>
import formApi from "@/apis/form";
import { updateQueryFromRoute, updateUrlWithoutReload } from "@/utils/updateUrl";
import moment from "moment";
import CustomPagination from "@/components/Page/Dashboard/CustomPagination.vue";
import {mapActions, mapGetters, mapState} from "vuex";
import deepGet from "lodash/get";

const initialQuery = {
  page: 1,
  per_page: 10,
  keyword: "",
  sort_by: "created_at",
  order_by: "desc",
};

export default {
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    active: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.init();
        }
      }
    }
  },
  components: {
    CustomPagination,
  },
  data() {
    return {
      totalRows: 0,
      loading: false,
      defaultFields: [
        {
          key: "avatar_url",
          label: "",
          sortable: false,
        },
        {
          key: "name",
          label: "顯示名稱",
          sortable: false,
        },
        {
          key: "outer_code",
          label: "識別編號",
          sortable: false,
        },
        {
          key: "mobile_phone",
          label: "手機",
          sortable: false,
        },
        {
          key: "email",
          label: "Email",
          sortable: false,
        },
      ],
      fields: [],
      results: [],
      query: { ...initialQuery },
      form: {
        title: null,
      },
    }
  },
  computed: {
    ...mapGetters('dashboardModule', ["getModule"]),
    ...mapState('general', ['organization']),
    canImportRegistrations() {
      if (!this.form || !this.form.subjects) return false;

      const hasNameField = this.form.subjects.some(subject =>
        subject.config?.assign_to_customer_data === 'extra.name'
      );
      const hasPhoneField = this.form.subjects.some(subject =>
        subject.config?.assign_to_customer_data === 'mobile_phone'
      );
      const isManualCheckinEnabled = this.form.config?.event_setting?.enable_manual_checkin ?? false;

      return hasNameField && hasPhoneField && isManualCheckinEnabled;
    },
    autoFilledSubjectIds() {
      if (!this.form || !this.form.subjects) return [];

      return this.form.subjects
        .filter((subject) => {
          const assignTo = subject.config?.assign_to_customer_data;
          return assignTo === 'extra.name' || assignTo === 'mobile_phone';
        })
        .map(subject => subject.id);
    },
  },
  methods: {
    ...mapActions('dashboardModule', ['fetchModule']),
    async fetchResults () {
      try {
        this.fields = [...this.defaultFields];
        const response = await formApi.getQuestionnaireRegistrants(this.$route.params.id, this.query);
        this.results = response.data.data;

        this.totalRows = response.data.meta.total;
        this.form = response.data.form;

        // 拿出form模組，判斷是否需要塞入額外欄位 name & phone
        await this.fetchModule({ code: 'form', org_id: this.organization.id })
        let showSubjects = deepGet(this.getModule("form"), 'module_config.showresult_in_questionnaire_registrants', false);
        let showCustomerData = deepGet(this.getModule("form"), 'module_config.show_customer_data_in_questionnaire_registrants', false);

        let subjects = [];
        if (showSubjects) {
          subjects = this.form.subjects.map((subject) => {
            return {
              key: subject.id,
              label: subject.config.title ?? '無標題',
            };
          })
        }

        const autoFilledSubjects = (this.canImportRegistrations && showCustomerData)
          ? this.form.subjects
            .filter((subject) => this.autoFilledSubjectIds.includes(subject.id))
            .map((subject) => ({
              key: subject.id,
              label: subject.config.title ?? '無標題',
            }))
          : [];

        const filteredSubjects = subjects.filter(
          (subject) => !this.autoFilledSubjectIds.includes(subject.key)
        );

        this.fields = [
          ...this.fields,
          ...filteredSubjects,
          ...autoFilledSubjects,
        ];

        // 把 results.responses 依照 subjects 攤開
        for (let i = 0; i < this.results.length; i ++) {
          let result = this.results[i];
          for (let j = 0; j < result.responses.length; j ++) {
            let response = result.responses[j];
            // 如果 response.value 是陣列，就轉成字串
            if (Array.isArray(response.value)) {
              response.value = response.value.join(', ');
            }
            this.results[i][response.subject_id] = response.value;
          }
        }

      } catch (error) {
        console.error(error);
        this.$swal("錯誤", "讀取表單結果錯誤", "error");
      }
    },
    async init() {
      this.loading = true
      this.query = updateQueryFromRoute(
        this.$route,
        this.$store,
        this.query,
      );
      await this.fetchResults();
      this.loading = false
    },
    async reset() {
      this.loading = true
      this.$set(this, 'query', { ...initialQuery });
      await this.fetchResults();
      this.loading = false
    },
    async runQueryAndUpdateUrl() {
      this.loading = true
      updateUrlWithoutReload(this.query, this.$store, this.$router);
      await this.fetchResults();
      this.loading = false
    },
    formatDate(value) {
      if (value) {
        return moment(String(value)).format("YYYY-MM-DD HH:mm");
      }
    },
    handleSort(ctx) {
      this.query.sort_by = ctx.sortBy;
      this.query.order_by = ctx.sortDesc ? "desc" : "asc";
      this.handlePageReset();
    },
    handlePageChange(newPage) {
      this.query.page = newPage;
      this.runQueryAndUpdateUrl();
    },
    handlePerPageChange(newPerPage) {
      this.query.per_page = newPerPage;
      this.query.page = 1;
      this.runQueryAndUpdateUrl();
    },
    async handlePageReset() {
      this.loading = true
      const currentPage = parseInt(this.query.page, 10);

      if (currentPage === 1) {
        updateUrlWithoutReload(this.query, this.$store, this.$router);
      } else {
        this.query.page = 1;
      }

      await this.fetchResults();
      this.loading = false
    },
    handleExport() {
      formApi.exportResults(this.form.id).then(() => {
        this.$swal({
          title: "成功",
          text: "可至“我的主控台”查看紀錄及下載檔案",
          showCancelButton: true,
          cancelButtonText: '確認',
          type: "success",
          confirmButtonText: "前往查看",
        }).then((result) => {
          if (result.value) {
            this.$router.push({
              name: "MyConsoleDownloadFileList",
              params: { org_code: this.$route.params.org_code },
            });
          }
        });
      });
    }
  },
}
</script>
